import { useTranslation } from 'react-i18next';
import CustomModal from '../../common/CustomModal';
import CustomButton from '../../../components/CustomButton';
import { CreateCampaignResult } from '../../../api/voucher/voucherApiTypes';
import { buildCampaignCsv, campaignCsvFileName } from '../../../utils/campaignCsv';
import { downloadCsv } from '../../../utils/csvDownload';
import { campaignCsvLabels } from './campaignCsvLabels';

// What a campaign create produced (#2249): how many codes, which entries were
// skipped and why, and the code list to download and send. Skipped entries are
// the backend's report — the tenant corrects them and mints a further campaign.

interface Props {
    result: CreateCampaignResult | null;
    onClose: () => void;
}

const CampaignResultModal = ({ result, onClose }: Props) => {
    const { t } = useTranslation();
    if (!result) return null;

    const download = () =>
        downloadCsv(
            campaignCsvFileName(result.campaign.name, t('vouchers_codes.campaign.csv.file_label')),
            buildCampaignCsv(
                result.codes.map((c) => ({
                    code: c.code,
                    recipientEmail: c.recipientEmail,
                    redeemed: false,
                    bookingNumbers: [],
                })),
                campaignCsvLabels(t),
            ),
        );

    return (
        <CustomModal
            open
            title={t('vouchers_codes.campaign.result.title')}
            onClose={onClose}
            closeButton={
                <CustomButton
                    title={t('vouchers_codes.campaign.result.done')}
                    isFilled={false}
                    onClick={onClose}
                />
            }
            saveButton={
                <CustomButton
                    title={t('vouchers_codes.campaign.result.download')}
                    onClick={download}
                />
            }
        >
            <div className={'flex flex-col gap-3'}>
                <p className={'text-sm text-foreground/80'}>
                    {t('vouchers_codes.campaign.result.created', { count: result.codes.length })}
                </p>
                {result.skipped.length > 0 && (
                    <div>
                        <p className={'text-sm font-medium text-foreground'}>
                            {t('vouchers_codes.campaign.result.skipped_title')}
                        </p>
                        <p className={'text-xs text-foreground/50'}>
                            {t('vouchers_codes.campaign.result.skipped_hint')}
                        </p>
                        <ul className={'mt-2 max-h-48 overflow-y-auto text-sm'}>
                            {result.skipped.map((entry, index) => (
                                <li
                                    key={`${entry.value}-${index}`}
                                    className={'flex justify-between gap-4 py-0.5'}
                                >
                                    <span className={'truncate text-foreground'}>
                                        {entry.value || t('common.missing')}
                                    </span>
                                    <span className={'shrink-0 text-foreground/60'}>
                                        {entry.reason === 'duplicate'
                                            ? t('vouchers_codes.campaign.result.reason_duplicate')
                                            : t('vouchers_codes.campaign.result.reason_invalid_email')}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                <p className={'text-xs text-foreground/50'}>
                    {t('vouchers_codes.campaign.once_hint')}
                </p>
            </div>
        </CustomModal>
    );
};

export default CampaignResultModal;
